import { Col, DatePicker, Form, FormInstance, Input, Row, Select } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import React from 'react';
import CustomButton from 'components/custom-button';
import dayjs, { Dayjs } from 'dayjs';

export enum ATTENDANCE_STATUS {
  NORMAL = 'normal',
  LATE = 'late',
  EARLY = 'early',
  LATE_EARLY = 'late-early',
  ABSENT = 'absent',
  NO_CHECKOUT = 'no-checkout',
  FORGOT_CHECKIN = 'forgot-checkin',
}

export const ATTENDANCE_STATUS_LABEL: Record<ATTENDANCE_STATUS, string> = {
  [ATTENDANCE_STATUS.NORMAL]: 'Đủ công',
  [ATTENDANCE_STATUS.LATE]: 'Checkin muộn',
  [ATTENDANCE_STATUS.EARLY]: 'Checkout sớm',
  [ATTENDANCE_STATUS.LATE_EARLY]: 'Thiếu checkin và checkout',
  [ATTENDANCE_STATUS.ABSENT]: 'Chưa có thông tin chấm công',
  [ATTENDANCE_STATUS.NO_CHECKOUT]: 'Chưa checkout',
  [ATTENDANCE_STATUS.FORGOT_CHECKIN]: 'Quên Checkin',
};

const { Option } = Select;

export interface AttendanceFilterFormValue {
  username: string;
  email: string;
  month: Dayjs | null;
  date: Dayjs | null;
  /** Giá trị ATTENDANCE_STATUS (normal, late, ...). */
  status?: string;
}

const initialValues: AttendanceFilterFormValue = {
  username: '',
  email: '',
  month: dayjs(),
  date: dayjs(),
  status: undefined,
};

interface Props {
  onSubmit: (value: any) => void;
  /** Form do trang giữ, để trang đặt/xoá ô TikTok ID khi URL đổi (?username=). */
  form?: FormInstance<AttendanceFilterFormValue>;
  /** Giá trị ban đầu lấy từ URL; nút "Xoá lọc" vẫn về mặc định (hôm nay). */
  preset?: Partial<AttendanceFilterFormValue>;
}

const Filter: React.FC<Props> = ({ onSubmit, form: formProp, preset }) => {
  const [ownForm] = Form.useForm<AttendanceFilterFormValue>();
  const form = formProp || ownForm;

  const onClearFilter = () => {
    // Về mặc định (hôm nay), không về giá trị lấy từ URL.
    form.setFieldsValue(initialValues);
    onSubmit({
      username: '',
      email: '',
      month: dayjs().format('YYYY-MM'),
      date: dayjs().format('YYYY-MM-DD'),
      status: undefined,
    });
  };

  const onFormSubmit = (value: AttendanceFilterFormValue) => {
    const { month, date, username, email, status } = value;
    const monthStr = month ? dayjs(month).format('YYYY-MM') : '';
    const dateStr = date ? dayjs(date).format('YYYY-MM-DD') : '';
    onSubmit({ username, email, month: monthStr, date: dateStr, status });
  };

  // Use vertical layout so labels sit above inputs — fixes the "Trạng thái"
  // label colliding with the wrapped Tiktok ID row in horizontal layout.
  // Grid: xl=6 (4 fields per row), lg=8 (3 per row), md=12 (2 per row), xs=24.
  return (
    <Form
      form={form}
      name="attendance-filter"
      layout="vertical"
      onFinish={onFormSubmit}
      initialValues={{ ...initialValues, ...preset }}
      className="custom-form"
    >
      <Row gutter={[16, 8]}>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="username" label="TikTok ID">
            <Input placeholder="Nhập TikTok ID" allowClear size="small" />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="email" label="Email">
            <Input placeholder="Nhập email" allowClear size="small" />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="month" label="Tháng">
            <DatePicker picker="month" allowClear={false} size="small" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="date" label="Ngày">
            <DatePicker placeholder="Cả tháng" size="small" style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="status" label="Trạng thái">
            <Select allowClear placeholder="Chọn trạng thái" size="small" style={{ width: '100%' }}>
              <Option value={ATTENDANCE_STATUS.NORMAL}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.NORMAL]}
              </Option>
              <Option value={ATTENDANCE_STATUS.LATE}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.LATE]}
              </Option>
              <Option value={ATTENDANCE_STATUS.EARLY}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.EARLY]}
              </Option>
              <Option value={ATTENDANCE_STATUS.LATE_EARLY}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.LATE_EARLY]}
              </Option>
              <Option value={ATTENDANCE_STATUS.ABSENT}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.ABSENT]}
              </Option>
              <Option value={ATTENDANCE_STATUS.NO_CHECKOUT}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.NO_CHECKOUT]}
              </Option>
              <Option value={ATTENDANCE_STATUS.FORGOT_CHECKIN}>
                {ATTENDANCE_STATUS_LABEL[ATTENDANCE_STATUS.FORGOT_CHECKIN]}
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <CustomButton
              bgColor="secondary-green"
              htmlType="submit"
              text="Tìm kiếm"
              icon={<SearchOutlined />}
            />
            <CustomButton
              text="Xoá lọc"
              bgColor="primary-grey"
              icon={<CloseOutlined />}
              onClick={onClearFilter}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default Filter;
